import { Inject, Injectable } from '@angular/core';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, AuthenticationResult, IPublicClientApplication, EventType, EventMessage } from '@azure/msal-browser';
import { filter, map, Observable, switchMap, tap, from, firstValueFrom, catchError, of, timer, timeout, race } from 'rxjs';
import { MSAL_ENTRA_INSTANCE } from '../../../msal-instances';
import { MsalInitializerService } from './msal-initializer.service';

@Injectable({ providedIn: 'root' })
export class EntraLoginService {
  private isInitializing = false;
  
  constructor(
    @Inject(MSAL_ENTRA_INSTANCE) private msalInstance: IPublicClientApplication,
    private broadcast: MsalBroadcastService,
    private msalInitializer: MsalInitializerService
  ) {
    // Initialize the MSAL instance when the service is instantiated
    this.ensureInitialized();
  }
  
  /**
   * Ensure the MSAL instance is initialized
   */
  private async ensureInitialized(): Promise<void> {
    if (this.isInitializing) {
      return;
    }
    
    this.isInitializing = true;
    try {
      await this.msalInitializer.initializeEntraInstance(this.msalInstance);
    } catch (error) {
      console.error('[EntraLoginService] Failed to initialize MSAL:', error);
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Initiates the Entra ID login redirect flow
   */
  async loginRedirect(loginHint?: string): Promise<void> {
    try {
      console.log('[EntraLoginService] Starting login redirect...');
      // Make sure MSAL is initialized before attempting to login
      await this.ensureInitialized();
      
      const request: any = {
        scopes: ['openid', 'profile', 'email']
      };
      
      // Add login hint if provided
      if (loginHint) {
        request.loginHint = loginHint;
      }
      
      console.log('[EntraLoginService] Starting login redirect with request:', request);
      await this.msalInstance.loginRedirect(request);
    } catch (error) {
      console.error('[EntraLoginService] Login redirect failed:', error);
      throw error;
    }
  }

  /**
   * Returns an Observable that handles the MSAL redirect promise
   * This is a more Angular-friendly approach to handle the redirect
   */
  handleRedirectObservable(): Observable<AuthenticationResult> {
    console.log('[EntraLoginService] Creating redirect observable...');
    
    // First ensure the MSAL instance is initialized
    return from(this.ensureInitialized()).pipe(
      tap(() => console.log('[EntraLoginService] MSAL initialized, handling redirect promise...')),
      // Then check for existing accounts
      switchMap(() => {
        // Check if we have accounts already
        const existingAccounts = this.msalInstance.getAllAccounts();
        
        if (existingAccounts.length > 0) {
          console.log('[EntraLoginService] Found existing account:', existingAccounts[0].name);
          return this.acquireTokenSilent$().pipe(
            tap(result => console.log('[EntraLoginService] Silent token acquired for existing account')),
            catchError(error => {
              console.error('[EntraLoginService] Error acquiring silent token:', error);
              throw error;
            })
          );
        }
        
        console.log('[EntraLoginService] No existing account, handling redirect...');
        
        // Create a simple promise-based approach that should work more reliably
        return from(new Promise<AuthenticationResult>((resolve, reject) => {
          console.log('[EntraLoginService] Setting up direct promise handling');
          
          // Set a timeout to prevent infinite waiting
          const timeoutId = setTimeout(() => {
            console.log('[EntraLoginService] Timeout occurred while waiting for authentication');
            reject(new Error('Timeout occurred while waiting for authentication'));
          }, 10000); // 10 seconds timeout
          
          // Handle the redirect
          this.msalInstance.handleRedirectPromise()
            .then(response => {
              clearTimeout(timeoutId);
              
              console.log('[EntraLoginService] Redirect promise completed:', response ? 'with result' : 'no result');
              
              if (response) {
                console.log('[EntraLoginService] Got direct result from handleRedirectPromise');
                resolve(response);
              } else {
                // If no response, check if we have accounts now
                const accounts = this.msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                  console.log('[EntraLoginService] Found account after redirect:', accounts[0].name);
                  // Try to acquire a token silently
                  this.msalInstance.acquireTokenSilent({
                    scopes: ['openid', 'profile', 'email'],
                    account: accounts[0]
                  })
                  .then(tokenResponse => {
                    console.log('[EntraLoginService] Token acquired silently');
                    resolve(tokenResponse);
                  })
                  .catch(silentError => {
                    console.error('[EntraLoginService] Silent token acquisition failed:', silentError);
                    reject(silentError);
                  });
                } else {
                  reject(new Error('No authentication result and no account found'));
                }
              }
            })
            .catch(error => {
              clearTimeout(timeoutId);
              console.error('[EntraLoginService] Error in handleRedirectPromise:', error);
              reject(error);
            });
        }));
      }),
      catchError(error => {
        console.error('[EntraLoginService] Critical error in authentication flow:', error);
        throw error;
      })
    );
  }

  /**
   * Handles a redirect response from Entra ID authentication
   * Returns an Observable that resolves when the redirect is complete
   * and the token has been acquired
   */
  onRedirectComplete$(): Observable<AuthenticationResult> {
    console.log('[EntraLoginService] Starting to handle redirect response...');
    
    // First ensure the MSAL instance is initialized
    return from(this.ensureInitialized()).pipe(
      tap(() => console.log('[EntraLoginService] MSAL initialized, handling redirect promise...')),
      // Then handle the redirect
      switchMap(() => {
        try {
          return from(this.msalInstance.handleRedirectPromise()).pipe(
            catchError(error => {
              console.error('[EntraLoginService] Error in handleRedirectPromise:', error);
              throw new Error(`MSAL handleRedirectPromise failed: ${error.message}`);
            })
          );
        } catch (error) {
          console.error('[EntraLoginService] Exception in handleRedirectPromise:', error);
          throw new Error(`Exception in handleRedirectPromise: ${error.message}`);
        }
      }),
      tap(response => console.log('[EntraLoginService] Redirect promise handled, response:', response ? 'exists' : 'null')),
      // Filter out null responses (happens when there's no redirect response to handle)
      filter(response => {
        const hasResponse = !!response;
        if (!hasResponse) {
          console.log('[EntraLoginService] No response from redirect, will try silent token acquisition');
        }
        return hasResponse;
      }),
      // If no response from handleRedirectPromise, try to get the token silently
      switchMap(response => {
        if (response) {
          console.log('[EntraLoginService] Using redirect response');
          return from(Promise.resolve(response));
        } else {
          console.log('[EntraLoginService] Trying to acquire token silently');
          // No response, so try to get the token silently
          return this.acquireTokenSilent$().pipe(
            catchError(error => {
              console.error('[EntraLoginService] Silent token acquisition failed:', error);
              throw new Error(`Silent token acquisition failed: ${error.message}`);
            })
          );
        }
      }),
      // Log the result
      tap(result => console.log('[EntraLoginService] Authentication result:', result)),
      catchError(error => {
        console.error('[EntraLoginService] Error handling redirect:', error);
        throw error;
      })
    );
  }

  /**
   * Subscribe to login success events from MSAL
   * This observable emits when a user successfully logs in or acquires a token
   */
  onLoginSuccess$(): Observable<EventMessage> {
    console.log('[EntraLoginService] Setting up login success event listener');
    return this.broadcast.msalSubject$.pipe(
      tap(msg => console.log('[EntraLoginService] Received MSAL event:', msg?.eventType, 'from', msg.interactionType)),
      filter((msg: EventMessage) => {
        // Check for any event that indicates successful authentication
        const isLoginSuccess = msg.eventType === EventType.LOGIN_SUCCESS;
        const isAcquireTokenSuccess = msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS;
        const isHandleRedirectSuccess = msg.eventType === EventType.HANDLE_REDIRECT_END && !msg.error;
        
        const isSuccess = isLoginSuccess || isAcquireTokenSuccess || isHandleRedirectSuccess;
        
        if (isSuccess) {
          console.log('[EntraLoginService] Detected authentication success event:', msg.eventType, 'with payload:', msg.payload ? 'exists' : 'none');
          
          // Check if we have accounts after this success event
          const accounts = this.msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            console.log('[EntraLoginService] Accounts available after success event:', accounts.length);
          }
        }
        
        return isSuccess;
      })
    );
  }

  /**
   * Attempts to acquire a token silently for the current user
   */
  acquireTokenSilent$(): Observable<AuthenticationResult> {
    return from(
      new Promise<AuthenticationResult>((resolve, reject) => {
        const accounts = this.msalInstance.getAllAccounts();
        
        if (accounts.length === 0) {
          reject(new Error('No accounts found'));
          return;
        }
        
        // Use the first account
        const account = accounts[0];
        
        this.msalInstance.acquireTokenSilent({
          account,
          scopes: ['openid', 'profile', 'email']
        })
        .then(result => resolve(result))
        .catch(error => reject(error));
      })
    );
  }
  
  /**
   * Get all accounts authenticated with Entra ID
   */
  getAllAccounts() {
    return this.msalInstance.getAllAccounts().filter(account => {
      // Only include non-B2C accounts (Entra accounts)
      return !account.idTokenClaims?.iss?.includes('b2clogin.com');
    });
  }
  
  /**
   * Logout the user from Entra ID
   */
  logout() {
    return this.msalInstance.logout();
  }
}